import React from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
} from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, Box } from '@mui/material';
import { Analytics } from '@vercel/analytics/react';
import './i18n';
import { DESIGN_TOKENS } from './theme/designTokens';
import Home from './pages/Home';
import Upload from './pages/Upload';
import HowItWorks from './pages/HowItWorks';
import About from './pages/About';
import Privacy from './pages/Privacy';
import Examples from './pages/Examples';
import ReportContents from './pages/ReportContents';
import GembaGuide from './pages/GembaGuide';
import Pricing from './pages/Pricing';
import Header from './components/Layout/Header';
import Footer from './components/Layout/Footer';
import NotFound from './pages/NotFound';
import LanguageManager from './components/LanguageManager';
import ScrollRestoration from './components/ScrollRestoration';

const theme = createTheme({
  palette: {
    primary: {
      main: '#4338CA',
      light: '#4F46E5',
      dark: '#3730A3',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#6D28D9',
      light: '#7C3AED',
      dark: '#5B21B6',
      contrastText: '#ffffff',
    },
    background: {
      default: '#FEFEFE',
      paper: '#F9FAFB',
    },
    text: {
      primary: '#111827',
      secondary: '#6B7280',
    },
    divider: '#E5E7EB',
    success: {
      main: '#10B981',
      light: '#34D399',
      dark: '#059669',
    },
    warning: {
      main: '#F59E0B',
      light: '#FBBF24',
      dark: '#D97706',
    },
    grey: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '3.5rem',
      lineHeight: 1.1,
      letterSpacing: '-0.025em',
      color: '#111827',
      '@media (max-width:900px)': {
        fontSize: '3rem',
      },
      '@media (max-width:600px)': {
        fontSize: '2.5rem',
      },
    },
    h2: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
      letterSpacing: '-0.025em',
      color: '#111827',
    },
    h3: {
      fontWeight: 600,
      fontSize: '2rem',
      lineHeight: 1.25,
      letterSpacing: '-0.02em',
      color: '#4F46E5',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.375,
      color: '#111827',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.375,
      color: '#111827',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.375,
      color: '#111827',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      color: '#4B5563',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      color: '#4B5563',
    },
    button: {
      fontWeight: 500,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: DESIGN_TOKENS.borderRadius.md,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: DESIGN_TOKENS.borderRadius.sm,
          padding: '12px 24px',
          fontWeight: 500,
          fontSize: '0.875rem',
          transition: 'all 0.2s ease-in-out',
        },
        containedPrimary: {
          backgroundColor: '#4338CA',
          color: '#ffffff',
          boxShadow: DESIGN_TOKENS.shadows.button.default,
          '&:hover': {
            backgroundColor: '#3730A3',
            boxShadow: DESIGN_TOKENS.shadows.button.hover,
            transform: 'translateY(-1px)',
          },
          '&:focus': {
            backgroundColor: '#3730A3',
            boxShadow: DESIGN_TOKENS.shadows.button.focus,
          },
        },
        outlined: {
          borderColor: '#E5E7EB',
          color: '#374151',
          '&:hover': {
            backgroundColor: '#F9FAFB',
            borderColor: '#D1D5DB',
            transform: 'translateY(-1px)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: 'none',
          borderRadius: DESIGN_TOKENS.borderRadius.md,
          border: '1px solid #E5E7EB',
        },
        elevation1: {
          boxShadow: 'none',
        },
        elevation2: {
          boxShadow: 'none',
        },
        elevation3: {
          boxShadow: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderRadius: DESIGN_TOKENS.borderRadius.md,
          border: '1px solid #E5E7EB',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            borderColor: '#4338CA',
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h1: {
          color: '#111827',
        },
        h2: {
          color: '#111827',
        },
        h3: {
          color: '#4F46E5',
        },
        h4: {
          color: '#4338CA',
        },
        h5: {
          color: '#111827',
        },
        h6: {
          color: '#111827',
        },
        body1: {
          color: '#4B5563',
        },
        body2: {
          color: '#4B5563',
        },
      },
    },
  },
});

const AppLayout = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
    <Header />
    <LanguageManager />
    
    {/* Global scroll restoration */}
    <ScrollRestoration 
      enabled={true}
      restoreDelay={50}
      forceTopPaths={['/upload']} // Force scroll to top for upload page
      shouldRestore={(location) => {
        // Custom logic to determine when to restore scroll
        // Don't restore on form submissions or specific states
        return !(location as any).state?.preventScrollRestore;
      }}
    />
    
    <Box 
      component="main" 
      sx={{ flexGrow: 1, pt: { xs: 4, sm: 6, md: 8 } }}
      aria-label="Main content"
    >
      <Outlet />
    </Box>
    <Footer />
  </Box>
);

const router = createBrowserRouter([
  {
    element: <AppLayout />,
    children: [
      { path: '/', element: <Home /> },
      { path: '/upload', element: <Upload /> },
      { path: '/how-it-works', element: <HowItWorks /> },
      { path: '/about', element: <About /> },
      { path: '/privacy', element: <Privacy /> },
      { path: '/examples', element: <Examples /> },
      { path: '/report-contents', element: <ReportContents /> },
      { path: '/gemba-guide', element: <GembaGuide /> },
      { path: '/pricing', element: <Pricing /> },
      { path: '*', element: <NotFound /> },
    ],
  },
]);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <RouterProvider router={router} />
      <Analytics />
    </ThemeProvider>
  );
}

export default App;
